import React, { useEffect, useState } from "react";
import { GlobalService } from "../../shared";
import { X } from "lucide-react";
import { createStaticSkill, deleteUserSkills, getStaticSkills, useAppDispatch } from "../../../redux";
import { LevelEnum, LevelLabelMap } from "../../shared/model";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";

interface SkillsProps {
    open: boolean;
    skillsData: any;
    allStaticSkills: any;
    handleSave: (updatedRegularHours: any) => void;
    onClose: () => void;
}

const SkillsModal = ({ open, allStaticSkills, skillsData, handleSave, onClose }: SkillsProps) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState([]);
    const [newSkill, setNewSkill] = useState({ name: "", level: LevelEnum.beginner.toString() });
    const dispatch = useAppDispatch();
    const handleSubmit = e => {
        e.preventDefault();
           if (!formData || formData.length === 0) {
        GlobalService.showToast("error", t("skillsModal.noSkillsError"), t("skillsModal.addAtLeastOneSkill"));
        return;
    }
        handleSave(formData);
        onClose();
    };

   const addSkill = async () => {
    if (!newSkill.name.trim()) return;

    // Normalize name for comparison
    const normalizedNewName = newSkill.name.trim().toLowerCase();

    // ❌ Check if the skill already exists in formData
    const alreadyInForm = formData.some(skill => skill.name.toLowerCase() === normalizedNewName);
    if (alreadyInForm) {
        GlobalService.showToast("warning", t("skillsModal.duplicateSkill"), t("skillsModal.skillAlreadyAdded"));
        return;
    }

    // ✅ Check if skill exists in allStaticSkills
    const existingSkill = allStaticSkills.find(
        skill => skill.name.toLowerCase() === normalizedNewName
    );

    let skillId = "";
    let skillName = newSkill.name;

    if (existingSkill) {
        skillId = existingSkill.id;
        skillName = existingSkill.name;
    } else {
        const { payload } = await dispatch(createStaticSkill({ name: newSkill.name }));
        skillId = payload.id;
        skillName = payload.name;
    }

    const newSkillData = {
        name: skillName,
        level: { id: Number(newSkill.level) },
        endorsements: 0,
        skillId: { id: skillId },
    };

    setFormData([...formData, newSkillData]);
    setNewSkill({ name: "", level: LevelEnum.beginner.toString() });
};


    const removeSkill = (index: number) => {
        const skill = formData[index];

        // Only dispatch delete action if skill has an id (i.e., it's saved on the server)
        if (skill?.id) {
            dispatch(deleteUserSkills({ id: skill.id }));
        }

        // Remove from local state
        const updated = [...formData];
        updated.splice(index, 1);
        setFormData(updated);
    };

    useEffect(() => {
        if (skillsData) {
            const prefilled = skillsData.map(skill => ({
                id: skill.id,
                name: skill.name,
                level: Object.entries(LevelLabelMap).find(([, v]) => v === skill.level)?.[0] || "1",
                endorsements: skill.endorsements,
                skillId: skill.skillId, // for existing skill
            }));
            setFormData(prefilled);
        }
    }, [skillsData]);
    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="max-h-[80vh] overflow-y-auto text-left">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold mb-4">{t("skillsModal.title")}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
                            &times;
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                <input
                                    type="text"
                                    value={newSkill.name}
                                    onChange={e => setNewSkill({ ...newSkill, name: e.target.value })}
                                    placeholder={t("skillsModal.newSkillName")}
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                <select
                                    value={newSkill.level}
                                    onChange={e => setNewSkill({ ...newSkill, level: e.target.value })}
                                    className="w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                                    <option value={LevelEnum.beginner}>{t("skillsModal.beginner")}</option>
                                    <option value={LevelEnum.intermediate}>{t("skillsModal.intermediate")}</option>
                                    <option value={LevelEnum.advanced}>{t("skillsModal.advanced")}</option>
                                    <option value={LevelEnum.expert}>{t("skillsModal.expert")}</option>
                                </select>
                                <Button type="button" onClick={addSkill} className="w-full sm:w-auto">
                                    {t("skillsModal.add")}
                                </Button>
                            </div>
                            <div className="space-y-4">
                                {formData.map((skill, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 border rounded-lg">
                                        <div className="flex-1">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                <span className="font-medium">{skill.name}</span>
                                                <select
                                                    value={skill.level}
                                                    onChange={e => {
                                                        const updated = [...formData];
                                                        updated[index].level = e.target.value;
                                                        setFormData(updated);
                                                    }}
                                                    className="w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                                                    <option value={LevelEnum.beginner}>{t("skillsModal.beginner")}</option>
                                                    <option value={LevelEnum.intermediate}>{t("skillsModal.intermediate")}</option>
                                                    <option value={LevelEnum.advanced}>{t("skillsModal.advanced")}</option>
                                                    <option value={LevelEnum.expert}>{t("skillsModal.expert")}</option>
                                                </select>
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {skill.endorsements} {t("skillsModal.endorsements")}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeSkill(index)}
                                            className="ml-2 text-gray-400 hover:text-gray-600">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-end gap-4">
                            <Button type="button" variant="outline" onClick={onClose}>
                                {t("skillsModal.cancel")}
                            </Button>
                            <Button type="submit">{t("skillsModal.saveChanges")}</Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SkillsModal;
